'use client';

/**
 * useKPIThresholdAlerts
 *
 * Subscribes to Supabase realtime changes on duty_assignments, crew_members,
 * and violations. After each change, re-fetches KPIs and fires toast alerts
 * when any threshold is breached:
 *
 *   - Unassigned duties  > 5
 *   - Crew availability  < 70%
 *   - Violations spike   (≥3 new violations within a 5-minute window)
 */

import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import { loadKPIThresholds } from '@/app/settings/components/KPIThresholdsPanel';

// ── Cooldown: don't re-fire the same alert within this period ─────────────────
const ALERT_COOLDOWN_MS = 2 * 60 * 1000; // 2 minutes

type AlertKey = 'unassigned' | 'crew_avail' | 'violation_spike';

export function useKPIThresholdAlerts() {
  const isMounted = useRef(true);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track last-fired time per alert to enforce cooldown
  const lastFired = useRef<Record<AlertKey, number>>({
    unassigned: 0,
    crew_avail: 0,
    violation_spike: 0,
  });

  // Rolling window of violation timestamps for spike detection
  const violationTimestamps = useRef<number[]>([]);

  const canFire = useCallback((key: AlertKey): boolean => {
    const now = Date.now();
    if (now - lastFired.current[key] < ALERT_COOLDOWN_MS) return false;
    lastFired.current[key] = now;
    return true;
  }, []);

  const checkKPIs = useCallback(async () => {
    if (!isMounted.current) return;
    try {
      const supabase = createClient();
      const today = new Date().toISOString().split('T')[0];

      // Load thresholds fresh each check so Settings changes take effect immediately
      const thresholds = loadKPIThresholds();

      const [dutiesResult, crewResult] = await Promise.all([
        supabase
          .from('duty_assignments')
          .select('id, driver1_name, conductor_name')
          .eq('duty_date', today),
        supabase
          .from('crew_members')
          .select('id, status'),
      ]);

      if (!isMounted.current) return;

      const duties = dutiesResult.data ?? [];
      const crew = crewResult.data ?? [];

      // ── Unassigned duties threshold ──────────────────────────────────────
      const unassigned = duties.filter(
        (d) =>
          !d.driver1_name ||
          d.driver1_name === '—' ||
          !d.conductor_name ||
          d.conductor_name === '—'
      ).length;

      if (unassigned > thresholds.unassignedDutyLimit && canFire('unassigned')) {
        toast.warning(
          `⚠️ ${unassigned} duties unassigned today — ${unassigned - thresholds.unassignedDutyLimit} above threshold`,
          {
            duration: 7000,
            description: 'Assign crew to avoid service disruptions.',
          }
        );
      }

      // ── Crew availability threshold ──────────────────────────────────────
      const available = crew.filter((c) => c.status === 'Available').length;
      const total = crew.length;
      const availPct = total > 0 ? Math.round((available / total) * 100) : 100;

      if (availPct < thresholds.crewAvailabilityPct && canFire('crew_avail')) {
        toast.error(
          `🚨 Crew availability at ${availPct}% — below ${thresholds.crewAvailabilityPct}% threshold`,
          {
            duration: 8000,
            description: `Only ${available} of ${total} crew members are available.`,
          }
        );
      }
    } catch (err) {
      console.error('useKPIThresholdAlerts: checkKPIs error', err);
    }
  }, [canFire]);

  const scheduleKPICheck = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      checkKPIs();
    }, 600);
  }, [checkKPIs]);

  const handleViolationInsert = useCallback(() => {
    if (!isMounted.current) return;
    const now = Date.now();

    // Load thresholds fresh on each violation event
    const thresholds = loadKPIThresholds();
    const windowMs = thresholds.violationSpikeWindowMin * 60 * 1000;

    // Prune timestamps outside the rolling window
    violationTimestamps.current = violationTimestamps.current.filter(
      (t) => now - t < windowMs
    );
    violationTimestamps.current.push(now);

    if (
      violationTimestamps.current.length >= thresholds.violationSpikeCount &&
      canFire('violation_spike')
    ) {
      toast.error(
        `🚨 Violation spike detected — ${violationTimestamps.current.length} violations in the last ${thresholds.violationSpikeWindowMin} minutes`,
        {
          duration: 8000,
          description: 'Review the Violations module for details.',
        }
      );
    }
  }, [canFire]);

  useEffect(() => {
    isMounted.current = true;

    // Initial KPI check on mount
    checkKPIs();

    const supabase = createClient();

    // Watch duty_assignments for unassigned / crew-availability changes
    const dutyChannel = supabase
      .channel('kpi_threshold_duties')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'duty_assignments' },
        () => scheduleKPICheck()
      )
      .subscribe();

    // Watch crew_members for availability changes
    const crewChannel = supabase
      .channel('kpi_threshold_crew')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'crew_members' },
        () => scheduleKPICheck()
      )
      .subscribe();

    // Watch violations for spike detection
    const violationChannel = supabase
      .channel('kpi_threshold_violations')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'violations' },
        () => handleViolationInsert()
      )
      .subscribe();

    return () => {
      isMounted.current = false;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      supabase.removeChannel(dutyChannel);
      supabase.removeChannel(crewChannel);
      supabase.removeChannel(violationChannel);
    };
  }, [checkKPIs, scheduleKPICheck, handleViolationInsert]);
}
